import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  App,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd';

import {
  getCredenciales,
  crearCredenciales,
  actualizarCredenciales,
  eliminarCredenciales,
} from '../api/credenciales';
import type { ClienteCredencial } from '../api/credenciales';
import { RFC_REGEX } from '../utils/rfc';

const { Text } = Typography;

interface FormValues {
  rfc: string;
  nombre_cliente: string;
  contrasena: string;
  monedas_soportadas: string[];
}

const MONEDAS_SUGERIDAS = [
  { value: 'MXN', label: 'MXN' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

interface CargaClientesProps {
  /** Se dispara después de crear/editar/borrar un cliente exitosamente —
   * permite a un padre (ej. el autocomplete de RFC en "Nueva entrada" de
   * CargaArchivos) refrescar su propia copia del listado sin recargar la
   * página. */
  onClienteChange?: () => void;
}

export function CargaClientes({ onClienteChange }: CargaClientesProps = {}) {
  const { message } = App.useApp();
  const [clientes, setClientes] = useState<ClienteCredencial[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRfc, setEditingRfc] = useState<string | null>(null);
  const [form] = Form.useForm<FormValues>();
  const [certFile, setCertFile] = useState<UploadFile[]>([]);
  const [keyFile, setKeyFile] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCredenciales();
      setClientes(data.clientes);
    } catch {
      message.error('Error al cargar credenciales');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  function openModal(cliente?: ClienteCredencial) {
    setEditingRfc(cliente?.rfc ?? null);
    form.setFieldsValue({
      rfc: cliente?.rfc ?? '',
      nombre_cliente: cliente?.nombre_cliente ?? '',
      monedas_soportadas: cliente?.monedas_soportadas ?? ['MXN'],
    });
    setCertFile([]);
    setKeyFile([]);
    setModalVisible(true);
  }

  function closeModal() {
    setModalVisible(false);
    setEditingRfc(null);
    form.resetFields();
    setCertFile([]);
    setKeyFile([]);
  }

  async function handleSubmit(values: FormValues) {
    const cert = certFile[0]?.originFileObj;
    const key = keyFile[0]?.originFileObj;
    const contrasena = values.contrasena?.trim();

    if (!cert || !key || !contrasena) {
      message.warning('Se requieren el .cer, el .key y la contraseña');
      return;
    }

    // El <Upload> solo filtra en el diálogo; con drag&drop puede entrar otra
    // extensión. Validar acá y en beforeUpload evita mandar un .txt/.pdf.
    if (cert && !cert.name.toLowerCase().endsWith('.cer')) {
      message.error('El certificado debe ser un archivo con extensión .cer.');
      return;
    }
    if (key && !key.name.toLowerCase().endsWith('.key')) {
      message.error('La llave privada debe ser un archivo con extensión .key.');
      return;
    }

    const monedas = (values.monedas_soportadas ?? [])
      .map(m => m.trim().toUpperCase())
      .filter(Boolean);
    const monedasInvalidas = monedas.filter(m => !/^[A-Z]{3}$/.test(m));
    if (monedasInvalidas.length > 0) {
      message.error(`Monedas inválidas: ${monedasInvalidas.join(', ')}. Use códigos de 3 letras mayúsculas (ej. MXN, USD).`);
      return;
    }

    setSubmitting(true);
    try {
      const [certB64, keyB64] = await Promise.all([
        fileToBase64(cert),
        fileToBase64(key),
      ]);
      const passB64 = stringToBase64(contrasena);

      const payload = {
        rfc: values.rfc.trim().toUpperCase(),
        nombre_cliente: values.nombre_cliente.trim(),
        certificado_base64: certB64,
        llave_base64: keyB64,
        contrasena_base64: passB64,
        monedas_soportadas: monedas,
      };

      if (editingRfc) {
        const res = await actualizarCredenciales(editingRfc, payload);
        if (res.monedas_persistidas === false) {
          message.warning(
            res.message ??
            'Credenciales actualizadas. Verifica que el backend soporte monedas_soportadas.'
          );
        } else if (res.monedas_persistidas === true) {
          message.success('Credenciales actualizadas exitosamente');
        } else {
          message.info(
            'Credenciales actualizadas. Las monedas soportadas se aplicarán cuando el backend esté actualizado.'
          );
        }
      } else {
        const res = await crearCredenciales(payload);
        if (res.monedas_persistidas === false) {
          message.warning(
            res.message ??
            'Credenciales guardadas. Verifica que el backend soporte monedas_soportadas.'
          );
        } else if (res.monedas_persistidas === true) {
          message.success('Credenciales guardadas exitosamente');
        } else {
          message.info(
            'Credenciales guardadas. Las monedas soportadas se aplicarán cuando el backend esté actualizado.'
          );
        }
      }

      closeModal();
      fetchClientes();
      onClienteChange?.();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const msg = error?.response?.data?.error || 'Error al guardar credenciales';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const uploadValidator = (fileList: UploadFile[]) => {
    if (!fileList || fileList.length === 0) {
      return Promise.reject(new Error('Este archivo es requerido'));
    }
    return Promise.resolve();
  };

  async function handleDelete(rfc: string) {
    try {
      await eliminarCredenciales(rfc);
      message.success('Credenciales eliminadas');
      fetchClientes();
      onClienteChange?.();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const msg = error?.response?.data?.error || 'Error al eliminar credenciales';
      message.error(msg);
    }
  }

  const columns: ColumnsType<ClienteCredencial> = [
    {
      title: 'RFC',
      key: 'rfc',
      width: 160,
      render: (_, c) => <Tag style={{ fontFamily: 'monospace' }}>{c.rfc}</Tag>,
    },
    {
      title: 'Nombre del Cliente',
      dataIndex: 'nombre_cliente',
      key: 'nombre_cliente',
      ellipsis: true,
    },
    {
      title: 'Monedas',
      key: 'monedas',
      width: 160,
      render: (_, c) => (c.monedas_soportadas?.length
        ? c.monedas_soportadas.map(m => <Tag key={m}>{m}</Tag>)
        : <Text type="secondary" style={{ fontSize: 12 }}>MXN/USD/EUR (default)</Text>),
    },
    {
      title: 'Última Modificación',
      key: 'ultima_mod',
      width: 180,
      render: (_, c) =>
        c.ultima_modificacion
          ? new Date(c.ultima_modificacion).toLocaleString('es-MX')
          : '—',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, c) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(c)}
          />
          <Popconfirm
            title="Eliminar credenciales"
            description={`¿Eliminar credenciales de ${c.rfc}?`}
            onConfirm={() => handleDelete(c.rfc)}
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text type="secondary">
          Gestiona las credenciales FIEL (.cer, .key y contraseña) de cada cliente para las operaciones con el SAT.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
          Nuevo Cliente
        </Button>
      </div>

      <Card styles={{ body: { padding: 0 } }}>
        {clientes.length === 0 && !loading ? (
          <Empty description="No hay credenciales cargadas." style={{ padding: 32 }} />
        ) : (
          <Table<ClienteCredencial>
            rowKey="rfc"
            columns={columns}
            dataSource={clientes}
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: false, size: 'small' }}
            size="small"
            scroll={{ x: 760 }}
          />
        )}
      </Card>

      <Modal
        open={modalVisible}
        title={editingRfc ? `Editar Credenciales — ${editingRfc}` : 'Nuevas Credenciales FIEL'}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="rfc"
            label="RFC"
            rules={[
              { required: true, message: 'RFC es requerido' },
              { pattern: RFC_REGEX, message: 'RFC inválido' },
            ]}
          >
            <Input placeholder="XAXX010101000" disabled={!!editingRfc} />
          </Form.Item>

          <Form.Item
            name="nombre_cliente"
            label="Nombre del Cliente"
            rules={[{ required: true, message: 'Nombre es requerido' }]}
          >
            <Input placeholder="Ej. Acme Corporation" />
          </Form.Item>

          <Form.Item
            name="monedas_soportadas"
            label="Monedas soportadas"
            tooltip="Monedas que este cliente puede conciliar. Seleccione una o más de la lista."
            rules={[{ required: true, message: 'Elige al menos una moneda' }]}
          >
            <Select mode="multiple" options={MONEDAS_SUGERIDAS} placeholder="MXN, USD..." />
          </Form.Item>

          <Form.Item
            label="Certificado (.cer)"
            name="certificado"
            rules={[{ validator: () => uploadValidator(certFile) }]}
          >
            <Upload
              fileList={certFile}
              beforeUpload={(file) => validarUpload(file, '.cer', 'certificado', message) ? false : Upload.LIST_IGNORE}
              onChange={({ fileList: fl }) => setCertFile(fl.slice(-1))}
              accept=".cer"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Seleccionar .cer</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label="Llave Privada (.key)"
            name="llave"
            rules={[{ validator: () => uploadValidator(keyFile) }]}
          >
            <Upload
              fileList={keyFile}
              beforeUpload={(file) => validarUpload(file, '.key', 'llave privada', message) ? false : Upload.LIST_IGNORE}
              onChange={({ fileList: fl }) => setKeyFile(fl.slice(-1))}
              accept=".key"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Seleccionar .key</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            label="Contraseña de la FIEL"
            name="contrasena"
            rules={[{ required: true, message: 'La contraseña es requerida' }]}
          >
            <Input.Password
              placeholder="Contraseña de la llave privada"
              autoComplete="new-password"
            />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Button onClick={closeModal}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingRfc ? 'Actualizar' : 'Guardar'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

// Validación de extensión en el upload (además del `accept` del input).
function validarUpload(
  file: File,
  ext: string,
  desc: string,
  message: { error: (content: ReactNode) => void },
): boolean {
  if (file.name.toLowerCase().endsWith(ext)) return true;
  message.error(`El ${desc} debe ser un archivo con extensión ${ext}.`);
  return false;
}

function stringToBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string | null;
      if (!result) {
        reject(new Error('No se pudo leer el archivo'));
        return;
      }
      // data:*;base64,DATA → extraer solo la parte base64
      const commaIndex = result.indexOf(',');
      if (commaIndex >= 0) {
        resolve(result.slice(commaIndex + 1));
      } else {
        // Si no hay coma, asumir que ya es base64 puro
        resolve(result);
      }
    };
    reader.onerror = () => reject(new Error('Error leyendo el archivo'));
    reader.readAsDataURL(file);
  });
}
